import React, { useEffect, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from './ui/button';
import { CongratsMessage } from './CongratsMessage';
import { InactiveMessage } from './InactiveMessage';
import { useCreateWallet } from '../hooks/useCreateWallet';
import type { Form } from '../interfaces/form.interface';
import type { Metadata } from '../interfaces/metadata.interface';
import { createFieldSchema } from '../utils/validation-schemas';
import { renderRichText } from '../utils/rich-text';
import { FieldFactory } from './form-fields';
import { FormRetryError } from './FormRetryError';

interface InscriptionFormProps {
  metadata: Metadata[];
  configuration: Form;
  workspace?: Record<string, unknown>;
  templateId?: string;
  onSubmitOverride?: (data: Record<string, string | boolean | number | string[]>) => Promise<void> | void;
  onErrorChange?: (visible: boolean) => void;
}

export const InscriptionForm: React.FC<InscriptionFormProps> = ({
  metadata,
  configuration,
  workspace,
  templateId,
  onSubmitOverride,
  onErrorChange
}) => {
  const [completed, setCompleted] = useState(false);
  const [isSubmittingOverride, setIsSubmittingOverride] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lastValues, setLastValues] = useState<Record<string, string | boolean | number | string[]> | null>(null);
  const retryButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (submitError) {
      retryButtonRef.current?.focus();
    }
    if (typeof onErrorChange === 'function') {
      onErrorChange(!!submitError);
    }
  }, [submitError, onErrorChange]);

  // Crear esquema de validación dinámicamente
  const formSchema = z.object(
    Object.fromEntries(
      metadata.map((meta) => [
        meta.id,
        createFieldSchema(meta)
      ])
    )
  );

  type FormData = z.infer<typeof formSchema>;

  // Crear valores por defecto basados en metadata
  const getDefaultValue = (meta: Metadata): string | boolean | number | string[] => {
    // Si hay un valor en workspace, usarlo
    if (workspace && typeof workspace === 'object' && 'metadata' in workspace) {
      const workspaceMetadata = workspace.metadata as Record<string, unknown>;
      if (workspaceMetadata[meta.id] !== undefined) {
        const value = workspaceMetadata[meta.id];
        if (Array.isArray(value)) {
          return value as string[];
        }
        if (typeof value === 'string' || typeof value === 'boolean' || typeof value === 'number') {
          return value;
        }
      }
    }
    
    // Si hay un valor por defecto definido en metadata, usarlo
    if (meta.default !== null) {
      if (typeof meta.default === 'string' || typeof meta.default === 'boolean' || typeof meta.default === 'number') {
        return meta.default;
      }
    }

    // Valores por defecto según el tipo
    switch (meta.type) {
      case 'checkbox':
        return false;
      case 'select':
        if (meta.definition?.multiselect) {
          return [];
        }
        return '';
      case 'email':
      case 'text':
      default:
        return '';
    }
  };

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: Object.fromEntries(
      metadata.map((meta) => [
        meta.id,
        getDefaultValue(meta)
      ])
    ),
  });

  const { mutate: createWalletMutation, isPending } = useCreateWallet(templateId || '');

  const onSubmit = async (data: FormData) => {
    setSubmitError(null);
    setLastValues(data as unknown as Record<string, string | boolean | number | string[]>);
    if (onSubmitOverride) {
      try {
        setIsSubmittingOverride(true);
        await onSubmitOverride(data as unknown as Record<string, string | boolean | number | string[]>);
        setCompleted(true);
      } catch {
        setSubmitError('No se pudo enviar el formulario. Intenta nuevamente.');
      } finally {
        setIsSubmittingOverride(false);
      }
      return;
    }

    createWalletMutation(data as unknown as Record<string, string | boolean | number | string[]>, {
      onSuccess: () => {
        setCompleted(true);
      },
      onError: () => {
        setSubmitError('No se pudo enviar el formulario. Intenta nuevamente.');
      },
    });
  };

  if (configuration.status === 1) {
    return <InactiveMessage configuration={configuration} />;
  }

  if (completed) {
    return <CongratsMessage configuration={configuration} />;
  }

  if (submitError) {
    return (
      <FormRetryError
        configuration={configuration}
        message={'Revisa tu conexión e intenta de nuevo. Tus respuestas se han guardado y las verás al reintentar.'}
        onRetry={() => {
          setSubmitError(null);
          if (lastValues) {
            (form.reset as unknown as (values: Record<string, string | boolean | number | string[]>) => void)(lastValues);
          }
        }}
        retryButtonRef={retryButtonRef}
      />
    );
  }

  return (
    <div className="space-y-6">
      {configuration.theme?.content?.subtitle && (
        <p className="text-xl font-semibold" style={{ color: configuration.theme?.visual?.textColor || '#333333' }}>
          {renderRichText(configuration.theme.content.subtitle)}
        </p>
      )}
      
      {configuration.theme?.content?.description && (
        <p style={{ color: configuration.theme?.visual?.textColor || '#333333' }}>
          {renderRichText(configuration.theme.content.description)}
        </p>
      )}

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {metadata.map((meta, index) => (
            <FieldFactory
              key={meta.id}
              meta={meta}
              index={index}
              configuration={configuration}
            />
          ))}
          
          <div className="flex justify-center pt-6">
            <Button
              type="submit"
              disabled={isPending || isSubmittingOverride}
              className="cursor-pointer"
              style={{
                background: configuration.theme?.buttons?.style?.background || '#1890ff',
                borderColor: configuration.theme?.buttons?.style?.background || '#1890ff',
                color: configuration.theme?.buttons?.style?.color || '#ffffff',
                borderRadius: '20px',
                height: '50px',
                padding: '0 100px',
                fontSize: '16px'
              }}
            >
              {isPending || isSubmittingOverride ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                configuration.theme?.buttons?.text?.confirm || 'Confirmar'
              )}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}; 